import React from 'react';
import { StageSection, Prose, KeyPoints, FactGrid } from '../../ContentPrimitives';
import { StageBlock, content, question } from '../../types';

export const meta = {
    title: 'Tokens & Standards',
    tagline: 'Why an ERC number matters more than the token\'s name.',
};

// This stage expects 2 quiz questions configured in the admin panel, in this order:
//   Q0 - what an ERC-20 token contract actually is (bookkeeping vs. a "real" asset)
//   Q1 - the purpose of approve/transferFrom as a two-step pattern
export const blocks: StageBlock[] = [
    content('what-is-a-token', () => (
        <StageSection gradientBorder eyebrow="Stage 7 of 8" title="A token is just a contract with a ledger inside it">
            <Prose>
                There's no separate "token layer" on Ethereum. An ERC-20 token is an ordinary contract that keeps a
                mapping from address to balance, and exposes functions to move value between entries in that
                mapping. Nothing about it is special to the protocol; the EVM doesn't know or care that a
                particular contract represents a token.
            </Prose>
            <Prose>
                What makes it useful is that every wallet, exchange, and dapp agrees to interact with it the same
                way, because the contract implements a shared <strong>standard</strong>, a fixed set of function
                names and behaviors defined in an Ethereum Improvement Proposal (EIP), commonly referred to by its
                ERC number.
            </Prose>
        </StageSection>
    )),

    content('erc20-erc721', () => (
        <StageSection title="Fungible vs. non-fungible: ERC-20 and ERC-721">
            <FactGrid items={[
                ['ERC-20', 'The fungible token standard: balanceOf, transfer, approve, transferFrom. Every unit is interchangeable: one token equals any other token of the same contract.'],
                ['ERC-721', 'The non-fungible token (NFT) standard: ownerOf, transferFrom, tied to a specific tokenId. Each token is distinct, often pointing at unique metadata.'],
            ]} />
            <Prose>
                Both standards exist for the same reason: without an agreed interface, every exchange and wallet
                would need custom code for every single token contract. A standard turns "does my wallet support
                this token?" into "does this contract implement the standard correctly?"
            </Prose>
        </StageSection>
    )),

    question(0),

    content('approve-pattern', () => (
        <StageSection title="Why token transfers often need two steps">
            <Prose>
                A contract can't reach into your wallet and take your tokens on its own; only you (via a
                transaction from your EOA) can move tokens out of your own balance directly. So when a dapp needs to
                pull tokens from you (a decentralized exchange swapping your tokens, for example), ERC-20 defines a
                two-step pattern instead:
            </Prose>
            <KeyPoints items={[
                <><code>approve(spender, amount)</code>: you tell the token contract that a specific address is allowed to move up to <code>amount</code> of your tokens.</>,
                <><code>transferFrom(owner, recipient, amount)</code>: the approved spender then moves tokens out of your balance, up to what you approved.</>,
            ]} />
            <Prose>
                This is also a well-known source of real-world bugs: forgetting to reset an old approval, approving
                unlimited amounts out of convenience, or a contract mismanaging the allowances it was granted are
                all patterns worth recognizing before you start looking for exploits involving tokens.
            </Prose>
        </StageSection>
    )),

    question(1),
];
